import "./style.scss";

import { Modal } from "@mui/material";
import Checkmark from "assets/img/svg/checkmark";
import UrssafIcon from "assets/img/svg/urssafIcon";
import ActionCard from "components/shared/ActionCard";
import PreviewDeclaration from "components/shared/PreviewDeclaration";
import ResponsiveButton from "components/shared/ResponsiveStyledButton";
import { Spinner } from "components/shared/Spinner";
import { URSSAFModal } from "components/URSSAFModal";
import { getDeclarationCardsData } from "containers/DeclarationPage/cards_helpers";
import type { DeclarationsCardsData } from "containers/DeclarationPage/utils";
import { formatLocaleDateString } from "helpers/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import { fetchContributions, setContributionDeclared } from "store/contributionStore";
import { useAppDispatch, useAppSelector } from "store/store";

export type DeclarationStatus = "neutral" | "success" | "error";

export type TimeStatus = "current" | "history" | "future";

interface DeclarationPageProps {}

const DeclarationPage = (props: DeclarationPageProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { contributionList, contributionListStatus } = useAppSelector((state) => state.contribution);

    const [showURSSAFModal, setShowURSSAFModal] = useState(false);
    const [currentContribution, setCurrentContribution] = useState<IDeclaration>();
    const [lastDeclaredAt, setLastDeclaredAt] = useState(new Date());

    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const handleDeclared = async (contribution?: IDeclaration) => {
        // dispatch(fetchContributions())
        if (!contribution) return;
        dispatch(setContributionDeclared({ contribution }));
        setShowURSSAFModal(false);
    };

    const previewAction = async (contribution: IDeclaration) => {
        setShowPreviewModal(true);
        setCurrentContribution(contribution);
    };

    const declareAction = async (contribution: IDeclaration) => {
        setShowURSSAFModal(true);
        setCurrentContribution(contribution);
    };

    const cardsActions: DeclarationsCardsData[] = getDeclarationCardsData({
        contributionList,
        previewAction,
        declareAction,
    });

    const renderCompactElement = (type: "future" | "history") => {
        if (cardsActions?.length <= 0) return <></>;
        let cards = cardsActions.filter((item) => item.timeStatus == type);
        if (type == "future") cards = cards.slice(0, 4);

        return cards.map((card, index) => (
            <div className="declaration-page-compact-element" key={index}>
                <div className="declaration-page-compact-element-left">
                    <UrssafIcon />
                    <div className="declaration-page-compact-element-left-title">{card.title}</div>
                </div>
                <div className="declaration-page-compact-element-right">
                    {card.description}
                    {card.action instanceof Array
                        ? card.action.map((item, index) => (
                              <ResponsiveButton buttonType="white" onClick={item.onClick} key={index}>
                                  {item.icon}
                                  {item.label}
                              </ResponsiveButton>
                          ))
                        : card.action && (
                              <ResponsiveButton buttonType="white" onClick={card.action.onClick}>
                                  {card.action.icon}
                                  {card.action.label}
                              </ResponsiveButton>
                          )}
                </div>
            </div>
        ));
    };

    return (
        <div className="declaration-page">
            <Modal open={showURSSAFModal} onClose={() => setShowURSSAFModal(false)}>
                <URSSAFModal
                    contribution={currentContribution}
                    quarter={currentContribution?.quarter ?? -1}
                    year={currentContribution?.year ?? -1}
                    onDeclared={handleDeclared}
                    onClose={() => setShowURSSAFModal(false)}
                />
            </Modal>
            <PreviewDeclaration
                showModal={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                contributionId={currentContribution?.id}
            />
            <div className="declaration-page-header">
                <h1>{t("Declare.Declare")}</h1>
                <div className="declaration-page-header-subtitle">{t("Declare.Subtitle")}</div>
            </div>
            <div className="separator"></div>
            {contributionListStatus == "pending" ? (
                <div style={{ textAlign: "center" }}>
                    <Spinner />
                </div>
            ) : (
                <div className="declaration-page-main">
                    <div className="declaration-page-main-wrapper">
                        <div className="declaration-page-card-list">
                            {cardsActions?.length > 0 ? (
                                cardsActions.every((item) => item.timeStatus != "current") ? (
                                    <ActionCard
                                        icon={<Checkmark />}
                                        title={t("Declare.DeclareURSSAFAllDoneTitle")}
                                        description={t("Declare.DeclareURSSAFAllDoneDescription", {
                                            date: formatLocaleDateString({ date: lastDeclaredAt }),
                                        })}
                                        style={{ backgroundColor: "#ECFAED" }}
                                    />
                                ) : (
                                    cardsActions
                                        .filter((item) => item.timeStatus == "current")
                                        .map((card, index) => <ActionCard {...card} key={index} />)
                                )
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="declaration-page-future">
                            <h2>{t("Declare.Future")}</h2>
                            {renderCompactElement("future")}
                        </div>
                        <div className="declaration-page-history">
                            <h2>{t("Declare.History")}</h2>
                            {renderCompactElement("history")}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeclarationPage;
